import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { combineLatest, Subject } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';
import { Option } from 'src/app/constants/option';
import { CutiDate } from 'src/app/models/cuti-date.t';
import { Cuti } from 'src/app/models/cuti.t';
import { GeneralOption } from 'src/app/models/general-option.t';
import { User } from 'src/app/models/user';
import { ApplicationService } from 'src/app/services/application.service';
import { CutiService } from 'src/app/services/cuti/cuti.service';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { TokenStorageService } from 'src/app/services/token-storage/token-storage.service';
import { DateUtils } from '../../../utils/date-utils';
import { FormUtils } from '../../../utils/form-utils';

@Component({
  selector: 'app-form-cuti',
  templateUrl: './form-cuti.component.html',
  styleUrls: ['./form-cuti.component.css']
})
export class FormCutiComponent implements OnInit, AfterViewInit, OnDestroy {
  isNew = !this.data;
  isLoading = false;
  nomor = new FormControl('');
  jenis = new FormControl(Option.default.id, [Validators.required]);
  alasan = new FormControl('', [Validators.required]);
  address = new FormControl('', [Validators.required]);
  jumlah = new FormControl(0, [Validators.required]);
  telp = new FormControl('', [Validators.required]);
  uploadFileForm = new FormControl();
  FormUtils = FormUtils;

  fromDate = new FormControl('');
  toDate = new FormControl('');
  dates: CutiDate[] = [];

  form: any = {

  };

  onDestroy$ = new Subject<void>();
  jenisCutis: GeneralOption[] = [];

  currentProfile: User | undefined;

  constructor(
    public matDialogRef: MatDialogRef<FormCutiComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Cuti,
    public tokenStorageService: TokenStorageService,
    private cutiService: CutiService,
    private snackBar: MatSnackBar,
    private applicationService: ApplicationService,
    private profileService: ProfileService,
  ) {
  }

  ngAfterViewInit(): void {
    if (!this.isNew) {
      this.nomor.setValue(this.data.nomor);
      this.jenis.setValue(this.data.jenisId);
      this.alasan.setValue(this.data.alasan);
      this.jumlah.setValue(this.data.jumlahHari);
      this.telp.setValue(this.data.telp);
      this.address.setValue(this.data.address);
      this.dates = this.data.dates!;
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  ngOnInit(): void {
    this.applicationService.data$.pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => {
        this.jenisCutis = data.jenisCutis;
      });
    this.profileService.currentProfile$.pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => {
        this.currentProfile = data;
        if (this.isNew) {
          this.telp.setValue(this.currentProfile.phone);
          this.address.setValue(this.currentProfile.address);
        }
      });
    this.profileService.loadUser(this.tokenStorageService.getId());

    combineLatest([this.fromDate.valueChanges, this.toDate.valueChanges])
      .pipe(delay(500), takeUntil(this.onDestroy$))
      .subscribe(([from, to]) => {
        if (this.fromDate.value && this.toDate.value) {
          const cutiDate: CutiDate = {
            fromDate: DateUtils.format(this.fromDate.value),
            toDate: DateUtils.format(this.toDate.value),
            jumlah: 0,
          }

          this.count(cutiDate);
        }
      });
  }

  prepareCuti(): Cuti {
    let cuti: Cuti = {
      nomor: this.nomor.value,
      jenisId: this.jenis.value,
      alasan: this.alasan.value,
      address: this.address.value,
      telp: this.telp.value,
      jumlahHari: this.jumlah.value,
      dates: this.dates,
      userId: this.currentProfile?.id,
    };

    if (!this.isNew) {
      cuti = {
        ...this.data,
        ...cuti,
        userId: this.data.userId,
      }
    }

    return cuti;
  }

  count(cutiDate?: CutiDate) {
    let cuti: Cuti = this.prepareCuti();
    if (cuti) {
      if (cutiDate) {
        const cutiDates = [...cuti.dates!, cutiDate];

        cuti = {
          ...cuti,
          dates: cutiDates,
        }
      }
      this.cutiService.count(cuti).subscribe((data) => {
        this.jumlah.setValue(data.count);
      });
    }
  }

  onSave(): void {
    this.addDate();
    const cuti = this.prepareCuti();
    if (cuti) {
      this.isLoading = true;
      this.cutiService.save(cuti).subscribe((cuti) => {
        this.showSnackBar('Pengajuan Cuti berhasil disimpan');
        this.matDialogRef.close(true);
        this.isLoading = false;
      });
    }
  }

  onSaveSubmit(): void {
    const cuti = this.prepareCuti();
    if (cuti) {
      this.isLoading = true;
      this.cutiService.saveSubmit(cuti).subscribe((cuti) => {
        this.showSnackBar('Pengajuan Cuti berhasil diajukan');
        this.matDialogRef.close(true);
        this.isLoading = false;
      });
    }
  }

  showSnackBar(message: string) {
    this.snackBar.open(message, 'OK', {
      duration: 3000,
    });
  }

  displayDate(item: CutiDate) {
    if (item.toDate && item.fromDate !== item.toDate) {
      return `${item.fromDate} sd ${item.toDate}`;
    }
    return item.fromDate;
  }

  addDate() {
    if (this.fromDate.value && this.toDate.value) {
      const cutiDate: CutiDate = {
        fromDate: DateUtils.format(this.fromDate.value),
        toDate: DateUtils.format(this.toDate.value),
        jumlah: 0,
      }
      this.dates = [
        ...this.dates,
        cutiDate,
      ];
      this.fromDate.setValue('');
      this.toDate.setValue('');

      this.count();
    }
  }

  removeDate(item: CutiDate) {
    const index = this.dates.indexOf(item);
    if (index > -1) {
      this.dates.splice(index, 1);
    }

    this.count();
  }
}
